import React, { Component } from 'react'
import { reduxForm } from 'redux-form'

import AdminProfilePresets from './adminProfilePresets'
import BadgesList from './badgesList'
import LoaderButton from '../../components/loaderButton'
import { badges } from './badges'
import { ReduxFormProps } from '../../forms'

export type Callback<T = void> = (...args: any) => T

const toBadgeOptions = (list: string[]) =>
  list.map((badge) => {
    return { label: badge, value: badge }
  })

export interface Props {
  cancel: Callback
  // Only superusers may view/assign the SUPERUSER badge.
  canAssignSuperuser?: boolean
}

export interface FormProps {
  badges: string[]
}

interface AdminBadgesFormState {
  selected: string[]
}

export class AdminBadgesForm extends Component<
  ReduxFormProps<Props, FormProps>,
  AdminBadgesFormState
> {
  static displayName = 'AdminBadgesForm'
  state: AdminBadgesFormState = {
    selected: [],
  }

  componentDidMount() {
    const { initialValues } = this.props
    const badges = initialValues?.badges
    if (badges && badges.length > 0) {
      this.setState({ selected: [...badges] })
    }
  }

  componentDidUpdate(prevProps: ReduxFormProps<Props, FormProps>) {
    const { initialValues } = this.props
    const prevBadges = prevProps.initialValues?.badges || []
    const currentBadges = initialValues?.badges || []

    // Update selected state when initialValues change (e.g., when switching between admins)
    if (
      prevBadges.length !== currentBadges.length ||
      !prevBadges.every((badge) => currentBadges.includes(badge))
    ) {
      this.setState({ selected: [...currentBadges] })
    }
  }

  render() {
    const {
      handleSubmit,
      submitting,
      cancel,
      initialValues,
      canAssignSuperuser = false,
    } = this.props

    const badgeOptions = toBadgeOptions(
      canAssignSuperuser
        ? badges
        : badges.filter((badge) => badge !== 'SUPERUSER')
    )

    const resetValues = () => {
      this.setState({ selected: [...(initialValues['badges'] || [])] })
    }

    const onChangePreset = (values) => {
      this.setState({ selected: values })
    }

    const onChangeBadges = (event) => {
      const value = event.target.value
      const selectedValues = [...this.state.selected]
      if (selectedValues.includes(value)) {
        selectedValues.splice(selectedValues.indexOf(value), 1)
      } else {
        selectedValues.push(value)
      }
      this.setState({ selected: selectedValues })
    }

    return (
      <form className="form" name="badgesForm" onSubmit={handleSubmit}>
        <div className="badges-form">
          <div>
            <h2 className="badges-form__header">Permissions</h2>
            <button
              className="badges-form__reset button button--link-text"
              type="button"
              aria-label="reset"
              data-testid="badges-form-reset"
              onClick={resetValues}
            >
              Reset
            </button>
          </div>
          <AdminProfilePresets
            onChange={onChangePreset}
            selectedBadges={this.state.selected}
          />
          <BadgesList
            name="badges"
            options={badgeOptions}
            selectedValues={this.state.selected}
            onChangeCallback={onChangeBadges}
          />
          <div className="admin-form__buttons">
            <LoaderButton
              data-testid="admin-user-create-form-create-button"
              type="submit"
              className="button button--primary-round admin-form__primary-button"
              isLoading={submitting}
            >
              Save
            </LoaderButton>
            <a
              onClick={cancel}
              data-testid="badges-form-cancel-button"
              className="button button--secondary-round"
              aria-label="cancel"
            >
              Cancel
            </a>
          </div>
        </div>
      </form>
    )
  }
}

export default reduxForm<FormProps, Props>({
  form: 'AdminBadgesForm',
  enableReinitialize: true,
  destroyOnUnmount: false,
})(AdminBadgesForm)
